import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'

// configs
import configs from '@/config'

// Load the replay integration lazily (its own async chunk) so the heavy replay
// recorder stays out of the initial parse. See services/sentryReplay.js.
const loadReplayModule = () => import(/* webpackChunkName: "sentry-replay" */ '@/services/sentryReplay')

// Run a callback once the browser is idle, so deferring replay never competes
// with first paint. Falls back to a short timeout where requestIdleCallback is
// unavailable (e.g. Safari).
const runWhenIdle = (cb) => {
  // No window in Jest/SSR — skip scheduling so we don't leak an open timer
  // handle or pull in the replay chunk in a non-browser environment.
  if (typeof window === 'undefined') return
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb, { timeout: 5000 })
  } else {
    setTimeout(cb, 3000)
  }
}

// Casting URLs are '/casting/:id/:shareCode' — collapse the volatile id +
// shareCode segments so every rejection on the same route groups together.
const normalizeCastingUrl = (url) => {
  if (typeof url !== 'string' || !url) return undefined

  try {
    const { pathname } = new URL(url, 'https://placeholder.local')
    return pathname.replace(/\/casting\/[^/]+\/[^/]+/, '/casting/:id/:shareCode')
  } catch {
    return undefined
  }
}

// Require all three casting keys, not any one: a vue-resource Response has
// url + status but no type/error, so matching on a single field mislabels it.
const isCastingRejection = (error) => (
  error != null &&
  typeof error === 'object' &&
  !(error instanceof Error) &&
  'error' in error &&
  'type' in error &&
  'url' in error
)

// A raw vue-resource Response ({ body, bodyText, headers, ok, status,
// statusText, url }) rejects some vuex actions instead of an Error. Require
// all three of ok/status/bodyText together — not one field — so a genuine
// Error carrying a stray `.status` (e.g. an HTTP client wrapper) is never
// mislabeled as a raw Response.
const isRawHttpResponse = (error) => (
  error != null &&
  typeof error === 'object' &&
  !(error instanceof Error) &&
  'ok' in error &&
  'status' in error &&
  'bodyText' in error
)

// Collapse a URL down to its pathname so every request to the same endpoint
// groups under one fingerprint regardless of query string or origin.
const normalizeEndpointUrl = (url) => {
  if (typeof url !== 'string' || !url) return undefined

  try {
    const { pathname } = new URL(url, 'https://placeholder.local')
    return pathname
  } catch {
    return undefined
  }
}

const httpResponseBodyMessage = (body) => {
  if (typeof body?.error === 'string') return body.error
  if (typeof body?.message === 'string') return body.message
  return undefined
}

// Extracted so classifyEvent stays a flat dispatch over each recognised
// shape rather than growing one branch's logic inline.
const relabelHttpResponseEvent = (event, error) => {
  const path = normalizeEndpointUrl(error.url) || 'unknown'
  const innerMessage = httpResponseBodyMessage(error.body)

  event.exception = {
    ...event.exception,
    values: [{
      ...event.exception?.values?.[0],
      type: 'HttpResponseError',
      value: `${path} [${error.status}]${innerMessage ? `: ${innerMessage}` : ''}`,
    }],
  }
  event.fingerprint = ['http-response-error', String(error.status), path]
  event.extra = {
    ...event.extra,
    httpResponseError: { status: error.status, url: path, message: innerMessage },
  }

  return event
}

// Somewhere in the casting flow a Promise rejects with a plain object
// ({ error, type, url }) instead of an Error, so Sentry's global
// onunhandledrejection handler can't extract a type — every such rejection
// collapses into one un-triageable "<unknown>" group. Relabel it into a
// stable, groupable issue instead of dropping it (a real casting failure
// still needs to surface). Mirrors the vue-resource Response classification
// in stpancras-audience-app's beforeSend (SYST-222).
const relabelCastingRejectionEvent = (event, error) => {
  const rejectionType = typeof error.type === 'string' && error.type
    ? error.type
    : 'CastingPromiseRejection'
  const path = normalizeCastingUrl(error.url)
  const innerMessage = typeof error.error === 'string'
    ? error.error
    : error.error?.message

  event.exception = {
    ...event.exception,
    values: [{
      ...event.exception?.values?.[0],
      type: 'CastingPromiseRejection',
      value: `${rejectionType}${path ? ` ${path}` : ''}${innerMessage ? `: ${innerMessage}` : ''}`,
    }],
  }
  event.fingerprint = ['casting-promise-rejection', rejectionType, path || 'unknown']
  event.extra = {
    ...event.extra,
    castingRejection: { type: rejectionType, url: path, error: innerMessage },
  }

  return event
}

export const classifyEvent = (event, hint) => {
  const error = hint?.originalException

  if (isCastingRejection(error)) return relabelCastingRejectionEvent(event, error)

  // A raw vue-resource Response reaching here (0 and 4xx are already dropped
  // upstream in beforeSend, so in practice this is a 5xx) collapses vuex's
  // actionLog.js `Sentry.captureException(error)` into an un-triageable
  // `<object>.error` group per request. Relabel into one signal per
  // endpoint+status — a real outage still surfaces, grouped, not dropped.
  if (isRawHttpResponse(error)) return relabelHttpResponseEvent(event, error)

  return event
}

const MQTT_CALLBACK_TEARDOWN_MESSAGE = 'Callback called multiple times'

const isMqttOrReadableStreamFrame = (frame) => (
  typeof frame?.filename === 'string' &&
  (frame.filename.includes('node_modules/mqtt/') || frame.filename.includes('node_modules/readable-stream/'))
)

// mqtt.js's browser WebSocket writer (layered on readable-stream) invokes the
// write callback more than once during a socket teardown/error race, which
// readable-stream reports as this NodeError. Every frame is third-party (no
// app code involved), so it carries no actionable signal — match narrowly on
// both the exact message AND an mqtt/readable-stream frame so a same-message
// error from app code still surfaces.
export const isMqttCallbackTeardownEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => (
    value?.value === MQTT_CALLBACK_TEARDOWN_MESSAGE &&
    (value.stacktrace?.frames || []).some(isMqttOrReadableStreamFrame)
  ))
}

const PICKR_TEARDOWN_MESSAGES = new Set([
  "Cannot read properties of null (reading 'clone')",
  "Cannot read properties of null (reading 'changestop')",
])

const isPickrFrame = (frame) => (
  typeof frame?.filename === 'string' && frame.filename.includes('@simonwep/pickr')
)

// @simonwep/pickr dereferences null inside _tapmove/palette.onchange when a
// pointer-move fires after the picker has already been destroyed. Every
// frame is third-party, so match narrowly on the exact message AND a pickr
// frame — the mqtt precedent above — so a same-message error from our own
// bundle still reports.
export const isPickrTeardownEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => (
    PICKR_TEARDOWN_MESSAGES.has(value?.value) &&
    (value.stacktrace?.frames || []).some(isPickrFrame)
  ))
}

const isGtmFrame = (frame) => (
  typeof frame?.filename === 'string' && /gtm\.js/i.test(frame.filename)
)

const isOwnAppFrame = (frame) => (
  typeof frame?.filename === 'string' &&
  (frame.filename.includes('/js/') || frame.filename.includes('webpack://'))
)

// gtm.js's internal error bubbles to the page's global scope, so Sentry
// blames whichever app frame happens to be on top of the stack — the
// existing /gtm.js denyUrls entry only matches the LAST valid frame URL, so
// it never catches this. Require an actual gtm.js frame AND no frame from
// our own bundle, so a real app ReferenceError sharing this message still
// reports.
export const isGtmGlobalScopeErrorEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => {
    const frames = value?.stacktrace?.frames
    if (!Array.isArray(frames)) return false

    return frames.some(isGtmFrame) && !frames.some(isOwnAppFrame)
  })
}

const ZOID_COMPONENT_CLOSED_MESSAGE = 'Component closed'

const isZoidFrame = (frame) => (
  typeof frame?.filename === 'string' && frame.filename.includes('node_modules/zoid/')
)

// zoid rejects its own internal ZalgoPromise with this message from inside
// its dispatch/onSuccess machinery, never reaching our closeZoid() try/catch.
// Match narrowly on the exact message AND a zoid frame so a same-message
// error from app code still reports.
export const isZoidInternalComponentClosedEvent = (event) => {
  const values = event?.exception?.values
  if (!Array.isArray(values)) return false

  return values.some((value) => (
    value?.value === ZOID_COMPONENT_CLOSED_MESSAGE &&
    (value.stacktrace?.frames || []).some(isZoidFrame)
  ))
}

// filter out all the responses with status equal to 0 (timeout request)
const beforeSend = (event, hint) => {
  const error = hint?.originalException

  if (isMqttCallbackTeardownEvent(event)) return null
  if (isPickrTeardownEvent(event)) return null
  if (isGtmGlobalScopeErrorEvent(event)) return null
  if (isZoidInternalComponentClosedEvent(event)) return null

  if (error) {
    if (error.status === 0) return null

    if (error.status >= 400 && error.status < 500) return null
  }

  if (configs.env !== 'production') {
    // log error to browser console
    // for better visibility into exceptions
    if (event?.extra?.error) {
      // eslint-disable-next-line no-console
      console.error(event.extra.error)
    } else if (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  return classifyEvent(event, hint)
}

const init = () => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.init({
      release: configs.sentryRelease,
      dsn: configs.sentry,
      environment: configs.env,
      integrations: [
        browserTracingIntegration(),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.2,
      tracesSampleRate: 0.02,
      Vue,
      tracingOptions: {
        trackComponents: true,
      },
      ignoreErrors: [
        'The operation is insecure.',
        'fullscreen error',
        'null is not an object (evaluating \'a.error\')',
        'UET is not defined',
        'too much recursion',
        'Fullscreen request denied',
        'Maximum call stack size exceeded',
        'Unable to decode audio data',
        'Extension context invalidated.',
        'xhr poll error',
        'Cannot read property \'getReadModeConfig\' of undefined',
        '"Timeout"',
        'UnhandledRejection: Non-Error promise rejection captured with value: Timeout',
        'AbortError: The play() request was interrupted by a call to pause().',
        'can\'t access dead object',
        'this.editor is undefined',
        'Cannot read properties of undefined (reading \'setMode\')',
        'undefined is not an object (evaluating \'this.editor.setMode\')',
        'Detected container element removed from DOM',
      ],
      denyUrls: [
        /analytics\.tiktok\.com/i,
        /js\.zohocdn\.com/i,
        /salesiq\.zoho\.com/i,
        /www\.gstatic\.com/i,
        /googletagmanager\.com/i,
        /\/gtm\.js(\?|$)/i,
        /\/gtag\/js(\?|$)/i,
      ],
      // beforeSend helps filtering which issues can go into Sentry
      beforeSend,
      enableLogs: true,
    })
    // Attach replay after the app is interactive so its recorder chunk loads
    // off the first-paint critical path. Early errors (before it attaches) are
    // still reported — they just won't carry a replay buffer.
    runWhenIdle(() => {
      loadReplayModule().then((m) => m.ensureReplay()).catch(() => {})
    })
  } catch {
    // do nothing
  }
}

const identifyUser = (currentUser) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.setUser(currentUser)
  } catch {
    // do nothing
  }
}

const captureMessage = (message) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureMessage(message)
  } catch {
    // do nothing
  }
}

const captureException = (error) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureException(error)
  } catch {
    // do nothing
  }
}

export default {
  init,
  identifyUser,
  captureMessage,
  captureException,
}
